import React from "react";
import { EuiPageBody, EuiFlexGroup, EuiFlexItem } from "@elastic/eui";
import { HeroSectionHyperspeed } from "./hero-section";
import { AutonubilIntegrations } from "./integrations";
import { AutonubilTrustBand } from "./trust-band";
import { AutonubilHowItWorks } from "./how-it-works";
import { AutonubilDiagramsInAction } from "./diagrams-in-action";
import { AutonubilProductShowcase } from "./product-showcase";
import { AutonubilOperatingModels } from "./operating-models";
import { AutonubilFeatures } from "./features";
import { AutonubilWhyOpsanio } from "./why-opsanio";
import { AutonubilAbout } from "./about";
import { AutonubilHomeServcies } from "./services";
import { AutonubilPartnersSection } from "./partner";
import { AutonubilWhyNow } from "./why-now";
import { AutonubilOpsanioAR } from "./opsanio-ar";
import { AutonubilAlertFunnel } from "./alert-funnel";
import { AutonubilOpsaneScore } from "./opsane-score";
import { AutonubilTarnkappe } from "./tarnkappe";
import { AutonubilFinalSection } from "./final-section";
import { AutonubilHomepageFooter } from "./footer";
import AgentDownloadSection from "./agent-download";
import { Reveal } from "./motion";

export const customTheme = {
  colors: {
    LIGHT: {
      primary: "#07a0ae",
      accent: "#78c9d9",
    },
  },
};

export default function AutonubilHomepage() {
  // Product-first page: opsanio (the product) leads; the company (autonubil)
  // is deliberately kept in the background - demoted to a compact "About" block
  // low on the page and the footer. Title reflects the product, not the company.
  //
  // Flow follows the competitor "enterprise security" pattern, tightened to keep
  // the page scannable (Hick's / Miller's Law):
  //   hero → integrations wall → trust band → how it works → product in action →
  //   capabilities → sovereignty → why now → agent → (company) about + services →
  //   partners → final CTA → footer.
  //
  // Sections fade/lift in on scroll via <Reveal> (IntersectionObserver, gated by
  // prefers-reduced-motion in ./motion). The hero is above the fold, so it is
  // intentionally NOT wrapped - it animates on mount via its own CSS classes.
  React.useEffect(() => {
    const prev = document.title;
    document.title = "opsanio - Security, visibility & control, unified";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div
      style={{
        background: "var(--ak-page-bg)",
        color: "var(--ak-text)",
        // Belt-and-suspenders: clip any stray horizontal overflow from individual
        // sections so the page body never scrolls sideways (esp. on mobile).
        // `clip` (not `hidden`) avoids creating a scroll container / affecting
        // vertical scroll.
        overflowX: "clip",
      }}
    >
      <EuiPageBody>
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiFlexItem>
            <HeroSectionHyperspeed />
          </EuiFlexItem>

          {/* Integrations wall - "works with your existing stack", right under hero */}
          <EuiFlexItem>
            <AutonubilIntegrations />
          </EuiFlexItem>

          {/* Trust band - hard numbers + compliance/certifications */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilTrustBand />
            </Reveal>
          </EuiFlexItem>

          {/* How it works - the signature appliance data-flow diagram */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilHowItWorks />
            </Reveal>
          </EuiFlexItem>

          {/* Under the hood - animated product views (vuln chain / flows / certs) */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilDiagramsInAction />
            </Reveal>
          </EuiFlexItem>

          {/* From log to decision - event aggregation funnel (Keine/Strikt/Regel/Gruppen/Geräte/MITRE) */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilAlertFunnel />
            </Reveal>
          </EuiFlexItem>

          {/* Product in action - real dashboard screenshots, tabbed (replaces the
              former 9-slider appliance sprawl) */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilProductShowcase />
            </Reveal>
          </EuiFlexItem>

          {/* Operating models - how you can run opsanio (Managed / On-Prem / Cloud) */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilOperatingModels />
            </Reveal>
          </EuiFlexItem>

          {/* Capabilities */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilFeatures />
            </Reveal>
          </EuiFlexItem>

          {/* Tarnkappe - AI on sensitive data without losing control */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilTarnkappe />
            </Reveal>
          </EuiFlexItem>

          {/* Why opsanio - sovereignty pillars (navy band) */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilWhyOpsanio />
            </Reveal>
          </EuiFlexItem>

          {/* Opsane Score - one score, every framework as a lens */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilOpsaneScore />
            </Reveal>
          </EuiFlexItem>

          {/* Why now - regulatory drivers create urgency */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilWhyNow />
            </Reveal>
          </EuiFlexItem>

          {/* Opsanio AR - detect / investigate / recommend: the foundation a SOC is built on */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilOpsanioAR />
            </Reveal>
          </EuiFlexItem>

          {/* opsanio Agent */}
          <EuiFlexItem>
            <Reveal>
              <AgentDownloadSection />
            </Reveal>
          </EuiFlexItem>

          {/* Company in the background: compact About + services, kept low */}
          <EuiFlexItem>
            <Reveal>
              <AutonubilAbout />
            </Reveal>
          </EuiFlexItem>
          <EuiFlexItem>
            <Reveal>
              <AutonubilHomeServcies />
            </Reveal>
          </EuiFlexItem>
          <EuiFlexItem>
            <Reveal>
              <AutonubilPartnersSection />
            </Reveal>
          </EuiFlexItem>

          {/* Final conversion CTA */}
          <EuiFlexItem>
            <Reveal>
              <div style={{ padding: "0 clamp(24px, 5vw, 48px) 64px" }}>
                <AutonubilFinalSection />
              </div>
            </Reveal>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiFlexItem>
          <AutonubilHomepageFooter />
        </EuiFlexItem>
      </EuiPageBody>
    </div>
  );
}
